import { useOrganizationList, useUser } from '@clerk/nextjs';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { Banner } from '@/code/frontend/components/ui/banner/banner';
import { useToast } from '@/code/frontend/hooks/use-toast';
import { trpc } from '@/code/lib/utils/trpc';

const JOIN_RETRY_ATTEMPTS = 5;
const JOIN_RETRY_DELAY_MS = 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Surfaces any pending org invitation for the signed-in user (who isn't already
 * in an org), so they can accept it instead of silently falling into solo
 * onboarding. Accepting joins the org in Clerk and syncs the DB, then
 * hard-redirects to "/" so the normal post-login redirect re-runs with the new
 * org.
 */
export const PendingInvitationsBanner = () => {
  const { user, isLoaded: userLoaded } = useUser();
  // Only fetch invites for org-less users, so members make no extra Clerk
  // request per page. Membership is already loaded on the session — free read.
  const userHasOrg = (user?.organizationMemberships.length ?? 0) > 0;
  const shouldFetchInvites = userLoaded && !userHasOrg;
  const { isLoaded, userInvitations } = useOrganizationList(
    shouldFetchInvites ? { userInvitations: { infinite: true } } : undefined,
  );
  const { toast } = useToast();
  const joinOrg = trpc.organizations.joinExistingOrganization.useMutation();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  // Per-invite, so a retry after a failed sync doesn't re-accept in Clerk.
  const [clerkAcceptedInvitationId, setClerkAcceptedInvitationId] = useState<
    string | null
  >(null);
  // Held in state so the banner survives the accept → sync flow even after the
  // accepted invite drops off the pending list (no unrecoverable half-join).
  type PendingInvitation = NonNullable<typeof userInvitations.data>[number];
  const [acceptedInvitation, setAcceptedInvitation] =
    useState<PendingInvitation | null>(null);

  if (!isLoaded) return null;

  // Don't surface fresh invites once the user is in an org, but keep showing
  // the one mid-acceptance so the retry path stays reachable.
  const candidateInvitations =
    userHasOrg || userInvitations.isLoading
      ? []
      : (userInvitations.data ?? []).filter(
          (inv) => !dismissedIds.includes(inv.id),
        );
  // Prefer the in-flight invite, but still honor a dismiss of it.
  const invitation =
    acceptedInvitation && !dismissedIds.includes(acceptedInvitation.id)
      ? acceptedInvitation
      : candidateInvitations[0];
  if (!invitation) return null;

  const orgName = invitation.publicOrganizationData?.name ?? 'an organization';

  const handleAccept = async () => {
    setAcceptingId(invitation.id);
    setAcceptedInvitation(invitation);
    try {
      if (clerkAcceptedInvitationId !== invitation.id) {
        // Accept in Clerk → creates the membership (fires membership.created,
        // which only sets canOwnCampaigns).
        await invitation.accept();
        setClerkAcceptedInvitationId(invitation.id);
      }
      // Sync organizationId + migrate the user's objects into the org. Retry to
      // ride out Clerk replication lag before the membership is readable.
      for (let attempt = 1; attempt <= JOIN_RETRY_ATTEMPTS; attempt++) {
        try {
          await joinOrg.mutateAsync();
          break;
        } catch (err) {
          if (attempt === JOIN_RETRY_ATTEMPTS) throw err;
          await sleep(JOIN_RETRY_DELAY_MS);
        }
      }
      // Hard nav so getDefaultRedirectUrl re-runs fresh with the new org.
      window.location.assign('/');
    } catch {
      setAcceptingId(null);
      toast({
        variant: 'destructive',
        title: 'Failed to accept invitation',
        description: 'Please try again.',
      });
    }
  };

  return (
    <Banner
      icon={<Mail />}
      title={`You've been invited to join ${orgName}`}
      description="Accept to join the team and get started."
      actions={[
        {
          id: 'accept-invitation',
          children: 'Accept',
          loading: acceptingId === invitation.id,
          onClick: handleAccept,
        },
      ]}
      onClose={() => setDismissedIds((prev) => [...prev, invitation.id])}
    />
  );
};
